// Platform-agnostic orchestration core for the Adyen card-add (tokenization)
// flow — CPS-26589, Milestone B. This hook owns the in-sheet state machine and
// the three in-sheet BFF mutations; it does NOT mount any Adyen SDK and is not
// platform-specific. The native/web sheets consume its output via
// AdyenCardAddSheetProps; a status-driven parent renders the sheet + UI.
//
// It does NOT call adyenInitCardAdd — initiation happens once at flow start in
// the entry hook (useAddPaymentMethod), which hands this hook the resulting
// {cardId, transactionId}. The machine therefore starts at `methods`
// (/paymentMethods discovery), not at init.

import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';

import type {
  AdyenComponentKind,
  AdyenPaymentResult,
  AdyenStateData,
} from 'components/payment/adyenCardAddSheet.types';
import { ADYEN_APPLE_PAY_MERCHANT_SESSION } from 'graphql/mutations/adyenApplePayMerchantSession';
import { ADYEN_CREATE_PAYMENT } from 'graphql/mutations/adyenCreatePayment';
import { ADYEN_PAYMENT_METHODS } from 'graphql/mutations/adyenPaymentMethods';
import { ADYEN_SUBMIT_PAYMENT_DETAILS } from 'graphql/mutations/adyenSubmitPaymentDetails';
import type {
  AdyenApplePayMerchantSessionInput,
  AdyenApplePayMerchantSessionResult,
  AdyenCheckoutChannel,
  AdyenCreatePaymentInput,
  AdyenPaymentMethodsInput,
  AdyenPaymentMethodsResult,
  AdyenSubmitDetailsInput,
  AdyenWalletType,
  CountryCode,
} from 'types/generatedSchemaTypes';
import { trackAdyenCardAdd } from 'utils/adyenAnalytics';
import { getQaRefusalToken } from 'utils/qaRefusal';

// Maps the BFF wallet discriminator to both the Adyen /paymentMethods `type`
// discriminator (used to filter the discovery JSON) and the in-sheet
// component kind Tasks 5-7 mount. Single source of truth for the branch so
// discovery filtering and componentKind can never disagree.
const WALLET_BRANCH: Record<
  AdyenWalletType,
  { adyenType: string; componentKind: AdyenComponentKind }
> = {
  APPLE_PAY: { adyenType: 'applepay', componentKind: 'applepay' },
  GOOGLE_PAY: { adyenType: 'googlepay', componentKind: 'googlepay' },
};

// Plain card add (walletType absent) keeps the /paymentMethods JSON exactly
// as the BFF returned it — this is the pre-existing, unfiltered behavior.
// A wallet add narrows the `paymentMethods` array down to the single entry
// matching the requested wallet's Adyen `type`, so the sheet's SDK init only
// ever sees the one method it's meant to render. Any other top-level keys in
// the JSON (there are none today, but the shape is opaque/BFF-owned) are left
// untouched by the spread.
const filterPaymentMethodsForWallet = (
  paymentMethods: unknown,
  walletType: AdyenWalletType | undefined
): unknown => {
  if (!walletType) {
    return paymentMethods;
  }
  const adyenType = WALLET_BRANCH[walletType].adyenType;
  const parsed = paymentMethods as
    | { paymentMethods?: Array<{ type?: string }> }
    | null
    | undefined;
  if (!parsed || !Array.isArray(parsed.paymentMethods)) {
    return paymentMethods;
  }
  return {
    ...parsed,
    paymentMethods: parsed.paymentMethods.filter(
      (method) => method?.type === adyenType
    ),
  };
};

// True when a wallet add's filtered discovery came back empty (wallet-only:
// plain card adds never trip this).
const isEmptyWalletPaymentMethods = (
  filtered: unknown,
  walletType: AdyenWalletType | undefined
): boolean => {
  if (!walletType) {
    return false;
  }
  const parsed = filtered as { paymentMethods?: unknown[] } | null | undefined;
  return (
    Array.isArray(parsed?.paymentMethods) && parsed.paymentMethods.length === 0
  );
};

// Inputs supplied by the entry hook for the lifetime of one sheet. The ids come
// from a single adyenInitCardAdd that already ran upstream (plan decision 8: one
// initiate per sheet lifecycle).
export type UseAdyenCardAddInput = {
  cardId: string;
  transactionId: string;
  countryCode: CountryCode;
  checkoutChannel: AdyenCheckoutChannel;
  locale: string;
  returnUrl: string;
  origin?: string;
  // Gate (mirrors useAdyenCardPolling): the caller turns this off while the ids
  // are still placeholders (session not yet stashed) so mount discovery does not
  // fire adyenPaymentMethods with undefined ids. Defaults to true.
  enabled?: boolean;
  // Absent -> plain card add (unfiltered discovery, componentKind 'card'):
  // the pre-existing, unchanged behavior. Set -> stored-wallet add: discovery
  // is narrowed to the matching Adyen payment-method entry and componentKind
  // reflects the wallet, for Tasks 5-7 to pick the right in-sheet component.
  walletType?: AdyenWalletType;
};

// The /paymentMethods discovery result the sheets need to render the SDK, plus
// the checkoutAttemptId that ties subsequent create/submit calls to this attempt.
export type AdyenCardAddBootstrap = {
  clientKey: string;
  environment: string;
  paymentMethods: unknown;
  checkoutAttemptId: string;
};

// In-sheet state machine. `methods` = discovering payment methods; `ready` = sheet
// can render; `submitting`/`action`/`activating` = create/submit lifecycle;
// `refused`/`cancelled`/`timeout`/`error` = terminal-ish. `timeout` is reserved
// for the polling layer (a later task) and is part of the shared vocabulary.
export type AdyenCardAddStatus =
  | 'methods'
  | 'ready'
  | 'submitting'
  | 'action'
  | 'activating'
  | 'done'
  | 'refused'
  | 'cancelled'
  | 'timeout'
  | 'error';

export type UseAdyenCardAdd = {
  status: AdyenCardAddStatus;
  bootstrap?: AdyenCardAddBootstrap;
  refusalReasonCategory?: string | null;
  onSubmit: (state: AdyenStateData) => Promise<AdyenPaymentResult>;
  onAdditionalDetails: (state: AdyenStateData) => Promise<AdyenPaymentResult>;
  onResult: (result: AdyenPaymentResult) => void;
  onError: (error: unknown) => void;
  retry: () => void;
  // Which in-sheet component the caller (Tasks 5-7) should mount. Always
  // 'card' when the input's walletType is absent.
  componentKind: AdyenComponentKind;
};

// A sheet dismissal carries no real Adyen result code, so the sheets surface it
// through onResult with this synthetic code. Everything else routed to onResult
// is a terminal result already mapped by the create/submit handler.
const DISMISSAL_RESULT_CODE = 'CANCELLED';

// DEV/QA refusal injector for the card-add (CIT) flow. Adyen TEST maps
// paymentMethod.holderName=<token> to a forced refusal resultCode, so QA can
// exercise every decline path from the only frontend-triggerable /payments in
// this app. holderName is a native paymentMethod field that payment-service's
// tokenization override leaves intact, so it reaches Adyen verbatim. Gated to
// the TEST environment — a live build reports environment 'live', so this can
// never fire in production. The token source is platform-forked behind
// getQaRefusalToken (web: ?qaRefusal= query param; native: DevScreen selection).
const injectQaRefusal = (
  paymentMethod: unknown,
  environment: string
): unknown => {
  if (environment?.toLowerCase() !== 'test') {
    return paymentMethod;
  }
  const token = getQaRefusalToken();
  if (!token || typeof paymentMethod !== 'object' || paymentMethod === null) {
    return paymentMethod;
  }
  return { ...(paymentMethod as Record<string, unknown>), holderName: token };
};

export const useAdyenCardAdd = (
  input: UseAdyenCardAddInput
): UseAdyenCardAdd => {
  const {
    cardId,
    transactionId,
    countryCode,
    checkoutChannel,
    locale,
    returnUrl,
    origin,
    enabled = true,
    walletType,
  } = input;

  const componentKind: AdyenComponentKind = walletType
    ? WALLET_BRANCH[walletType].componentKind
    : 'card';

  const [status, setStatus] = useState<AdyenCardAddStatus>('methods');
  const [bootstrap, setBootstrap] = useState<AdyenCardAddBootstrap | undefined>(
    undefined
  );
  const [refusalReasonCategory, setRefusalReasonCategory] = useState<
    string | null | undefined
  >(undefined);

  const [adyenPaymentMethods] = useMutation<
    { adyenPaymentMethods: AdyenPaymentMethodsResult },
    { input: AdyenPaymentMethodsInput }
  >(ADYEN_PAYMENT_METHODS, {
    onCompleted: (data) => {
      const result = data?.adyenPaymentMethods;
      if (!result) {
        setStatus('error');
        return;
      }
      const filteredPaymentMethods = filterPaymentMethodsForWallet(
        result.paymentMethods,
        walletType
      );
      setBootstrap({
        clientKey: result.clientKey,
        environment: result.environment,
        paymentMethods: filteredPaymentMethods,
        checkoutAttemptId: result.checkoutAttemptId,
      });
      if (isEmptyWalletPaymentMethods(filteredPaymentMethods, walletType)) {
        // Nothing for the sheet's SDK to bind to (no applepay/googlepay entry
        // came back for this merchant/region). bootstrap is still set (above)
        // so the container's `!flow.bootstrap` gate mounts the sheet, which
        // renders its existing terminal `error` case (message + retry) instead
        // of the entry-state SDK tree — a dead, empty-payment-methods sheet.
        trackAdyenCardAdd('error');
        setStatus('error');
        return;
      }
      setStatus('ready');
    },
    onError: () => setStatus('error'),
  });

  const [adyenCreatePayment] = useMutation<
    { adyenCreatePayment: AdyenPaymentResult },
    { input: AdyenCreatePaymentInput }
  >(ADYEN_CREATE_PAYMENT);

  const [adyenSubmitPaymentDetails] = useMutation<
    { adyenSubmitPaymentDetails: AdyenPaymentResult },
    { input: AdyenSubmitDetailsInput }
  >(ADYEN_SUBMIT_PAYMENT_DETAILS);

  const [adyenApplePayMerchantSession] = useMutation<
    { adyenApplePayMerchantSession: AdyenApplePayMerchantSessionResult },
    { input: AdyenApplePayMerchantSessionInput }
  >(ADYEN_APPLE_PAY_MERCHANT_SESSION);

  // Apple Pay WEB merchant validation (own-certificate setup): exchanges
  // Safari's validationURL for Apple's merchant session via the BFF and
  // returns the opaque session for completeMerchantValidation. Exposed
  // unconditionally — only the web sheet's applepay path ever calls it (in-app
  // Apple Pay has no onvalidatemerchant), so it is inert on native.
  // initiativeContext must be the exact registered domain serving the page,
  // i.e. the live hostname (globalThis.location only exists on web).
  const onValidateMerchant = useCallback(
    async (validationUrl: string): Promise<unknown> => {
      const initiativeContext =
        (globalThis as { location?: { hostname?: string } }).location
          ?.hostname ?? '';
      const response = await adyenApplePayMerchantSession({
        variables: { input: { validationUrl, initiativeContext } },
      });
      const merchantSession =
        response.data?.adyenApplePayMerchantSession?.merchantSession;
      if (!merchantSession) {
        throw new Error('Apple Pay merchant session missing from response');
      }
      return merchantSession;
    },
    [adyenApplePayMerchantSession]
  );

  // Discover payment methods. Runs once on mount and again on retry(); sends only
  // the minimal context — the BFF derives currency/channel/amount/match context.
  const discover = useCallback(() => {
    setStatus('methods');
    setRefusalReasonCategory(undefined);
    trackAdyenCardAdd('methods', { checkoutChannel });
    adyenPaymentMethods({
      variables: {
        // walletType rides along so the backend's resolveLocalPaymentMethod
        // offers the wallet method — its allowedPaymentMethods only include
        // applepay/googlepay when the method is explicitly requested.
        input: {
          cardId,
          countryCode,
          checkoutChannel,
          locale,
          ...(walletType ? { walletType } : {}),
        },
      },
    }).catch(() => {
      // onError already moves the machine to `error`; the rejection is swallowed
      // so an unhandled promise doesn't surface during the transient failure.
    });
  }, [
    adyenPaymentMethods,
    cardId,
    countryCode,
    checkoutChannel,
    locale,
    walletType,
  ]);

  useEffect(() => {
    // Mirror useAdyenCardPolling's enabled/skip gate: stay fully inert (status
    // `methods`, no adyenPaymentMethods fired, no bootstrap) until the caller
    // enables the flow AND both ids are real. The caller wires `enabled` to the
    // session presence, so this never fires discovery with placeholder ids.
    if (!enabled || !cardId || !transactionId) {
      return;
    }
    discover();
    // Fire discovery exactly once per mount; retry() re-runs it explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Shared terminal mapping for both create and submit results. AUTHORISED ->
  // activating (handed off to the polling layer); an action -> action (the sheet
  // drives 3DS/redirect, then calls onAdditionalDetails); REFUSED/FAILED ->
  // refused (surfacing the stable refusalReasonCategory bucket).
  const applyResult = useCallback((result: AdyenPaymentResult): void => {
    if (result.action) {
      // An SDK action (3DS2 / redirect) was returned; the sheet now drives it and
      // will call onAdditionalDetails when it completes.
      trackAdyenCardAdd('action_start');
      setStatus('action');
      return;
    }
    // Adyen returns PascalCase result codes ("Authorised", "Refused", "Pending"),
    // so normalize to uppercase before matching. Comparing the raw value against
    // the uppercase cases previously fell through to the error branch on a
    // successful "Authorised" — surfacing "something went wrong" after a good pay.
    switch (result.resultCode?.toUpperCase()) {
      // AUTHORISED is a sync success; PENDING is non-terminal (ADR-014
      // webhook-final semantics — resolved by the webhook, not this sync
      // response). Both hand off to activation polling, which owns the 60s
      // timeout, rather than treating the sync response as terminal or stranding
      // the submitting spinner.
      case 'AUTHORISED':
      case 'PENDING':
        trackAdyenCardAdd('authorised');
        setStatus('activating');
        break;
      case 'REFUSED':
      case 'FAILED':
        trackAdyenCardAdd('refused', {
          refusalReasonCategory: result.refusalReasonCategory,
        });
        setRefusalReasonCategory(result.refusalReasonCategory);
        setStatus('refused');
        break;
      default:
        // An unmapped/unknown result code: there is no path that resolves it, so
        // surface a terminal error rather than stranding on the submitting spinner.
        trackAdyenCardAdd('error');
        setStatus('error');
        break;
    }
  }, []);

  const onSubmit = useCallback(
    async (state: AdyenStateData): Promise<AdyenPaymentResult> => {
      if (!bootstrap) {
        throw new Error('useAdyenCardAdd: onSubmit called before bootstrap');
      }
      trackAdyenCardAdd('submit', { checkoutChannel });
      setStatus('submitting');
      try {
        const response = await adyenCreatePayment({
          variables: {
            input: {
              transactionId,
              cardId,
              checkoutAttemptId: bootstrap.checkoutAttemptId,
              paymentMethod: state.paymentMethod as never,
              browserInfo: state.browserInfo as never,
              returnUrl,
              countryCode,
              checkoutChannel,
              locale,
              origin,
              // Same explicit-method contract as discovery above.
              ...(walletType ? { walletType } : {}),
            },
          },
        });
        const result = response.data?.adyenCreatePayment;
        if (!result) {
          throw new Error('useAdyenCardAdd: empty adyenCreatePayment result');
        }
        applyResult(result);
        return result;
      } catch (error) {
        trackAdyenCardAdd('error');
        setStatus('error');
        throw error;
      }
    },
    [
      adyenCreatePayment,
      applyResult,
      bootstrap,
      cardId,
      checkoutChannel,
      countryCode,
      locale,
      origin,
      returnUrl,
      transactionId,
      walletType,
    ]
  );

  const onAdditionalDetails = useCallback(
    async (state: AdyenStateData): Promise<AdyenPaymentResult> => {
      if (!bootstrap) {
        throw new Error(
          'useAdyenCardAdd: onAdditionalDetails called before bootstrap'
        );
      }
      // The SDK action finished and is handing back the additional details to
      // submit — the completion half of the action_start emitted in applyResult.
      trackAdyenCardAdd('action_complete', { checkoutChannel });
      setStatus('submitting');
      try {
        const response = await adyenSubmitPaymentDetails({
          variables: {
            input: {
              cardId,
              transactionId,
              checkoutAttemptId: bootstrap.checkoutAttemptId,
              details: state.details as never,
              paymentData: state.paymentData,
            },
          },
        });
        const result = response.data?.adyenSubmitPaymentDetails;
        if (!result) {
          throw new Error(
            'useAdyenCardAdd: empty adyenSubmitPaymentDetails result'
          );
        }
        applyResult(result);
        return result;
      } catch (error) {
        trackAdyenCardAdd('error');
        setStatus('error');
        throw error;
      }
    },
    [
      adyenSubmitPaymentDetails,
      applyResult,
      bootstrap,
      cardId,
      checkoutChannel,
      transactionId,
    ]
  );

  // Terminal callback from the sheet. Real terminal results (AUTHORISED/REFUSED/
  // FAILED) are ALREADY mapped by the awaited create/submit handler, so they are an
  // intentional no-op here — the only state-changing case is a user dismissal,
  // signalled by the synthetic DISMISSAL_RESULT_CODE.
  // FOLLOW-UP (B-T4/T5): replace this synthetic code with a real cancel()/onDismiss
  // on AdyenCardAddSheetProps so dismissal isn't smuggled through the result channel.
  //
  // A dismissal only cancels a flow that has not been submitted yet. adyen-web's
  // Apple Pay sheet can report a late cancel after authorization (PassKit times
  // out waiting for completion), which must not abandon an activating or
  // failed flow.
  const onResult = useCallback(
    (result: AdyenPaymentResult): void => {
      if (result?.resultCode !== DISMISSAL_RESULT_CODE) {
        return;
      }
      if (status !== 'methods' && status !== 'ready' && status !== 'action') {
        return;
      }
      trackAdyenCardAdd('cancelled');
      setStatus('cancelled');
    },
    [status]
  );

  // Read through a ref: the sheets capture onError once at SDK init, so a
  // status-closure here would go stale and defeat the guard below.
  const statusRef = useRef<AdyenCardAddStatus>(status);
  statusRef.current = status;

  const onError = useCallback((_error: unknown): void => {
    // Once a submit handed off to activation the token is live at Adyen and
    // the poller owns terminality. A late SDK/session error (e.g. the web
    // Apple Pay sheet failing to complete after /payments already returned
    // AUTHORISED) must not tear the flow down: downgrading to `error` here
    // would stop the poll and let the container's abandon-cancel discard a
    // card the shopper successfully authorised.
    if (statusRef.current === 'activating') {
      return;
    }
    trackAdyenCardAdd('error');
    setStatus('error');
  }, []);

  // Re-run discovery with the SAME {cardId, transactionId}. Gated to the
  // transient `error` status only. A `refused` (or any non-error) outcome is NOT
  // retried here: a spent transaction needs a fresh adyenInitCardAdd (new ids),
  // which is the entry hook's job (plan decision 8).
  const retry = useCallback(() => {
    if (status !== 'error') {
      return;
    }
    discover();
  }, [status, discover]);

  return {
    status,
    bootstrap,
    refusalReasonCategory,
    onSubmit,
    onAdditionalDetails,
    onResult,
    onError,
    onValidateMerchant,
    retry,
    componentKind,
  };
};

export default useAdyenCardAdd;
